import React, { useEffect, useRef } from 'react';
import { deviceZone, formatIn, zoneNote } from '../../../function-utils/building-time';
import { status as statusTokens, palette } from '../../../style-utils/theme';
import {
  TableFrame,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../table/list-table';
import {
  Empty,
  ErrorBox,
  SkeletonRow,
  Btn,
  Pill,
  Mono,
  Scroller,
  Dense,
  StickyHead,
} from './admin.styled';

/**
 * THE CONSOLE'S TABLE, ON TOP OF THE ONE TABLE ENGINE.
 *
 * `components/table/list-table.tsx` is the repo's single table implementation
 * (WP-37 replaced seven). This does not start an eighth: it takes that engine's
 * primitives and adds the three things an operations list needs — a column
 * spec, the four states, and a "load more" that appends a cursor page instead
 * of replacing the rows.
 *
 * ── THE COLUMN SPEC IS THE WHOLE POINT ─────────────────────────────────────
 *
 * Twenty screens in this console are the same table with different columns. As
 * twenty bespoke <table>s they would drift within a month — the Buildings and
 * Portfolios screens in /dashboard already did exactly that, which is what
 * WP-37 was written to undo. One spec-driven component means a change to row
 * height, empty state or overflow behaviour lands everywhere at once.
 *
 * ── FOUR STATES, NOT TWO ───────────────────────────────────────────────────
 *
 * loading / empty / error / rows. A table that renders "no results" while a
 * request is in flight tells an operator their filter matched nothing when it
 * has not been asked yet, and that is how a real customer gets reported missing.
 */

export interface Column<T> {
  key: string;
  header: string;
  /** Right-aligned and tabular — for anything the eye compares down a column. */
  numeric?: boolean;
  /** Hidden below `lg`. Use for context that is nice, not necessary. */
  wide?: boolean;
  /** Percent, matching the engine's own `width` prop on Th. */
  width?: number;
  /** Never broken across lines — for a short value (a date and time) that a
   *  long neighbour would otherwise squeeze into a column a few letters wide. */
  nowrap?: boolean;
  render: (row: T) => React.ReactNode;
}

export type Tone = keyof typeof statusTokens;

/** A status pill whose text colour always matches its tint (design_system §2.3). */
export const Tag: React.FC<{ tone?: Tone; children: React.ReactNode }> = ({
  tone = 'neutral',
  children,
}) => {
  const t = statusTokens[tone];

  return (
    <Pill style={{ background: t.bg, color: t.fg }}>
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: t.dot,
          display: 'inline-block',
        }}
      />
      {children}
    </Pill>
  );
};

/** The vocabulary the whole console reads statuses through, in one place. */
export const toneFor = (value?: string | null): Tone => {
  const v = String(value || '').toLowerCase();
  if (['active', 'accepted', 'ok', 'sent', 'succeeded', 'trialing'].includes(v)) return 'success';
  if (['pending', 'queued', 'past_due', 'incomplete', 'paused', 'offline'].includes(v))
    return 'warning';
  if (['inactive', 'suspended', 'closed', 'canceled', 'cancelled', 'failed', 'unpaid', 'expired', 'revoked', 'archived'].includes(v))
    return 'alert';

  return 'neutral';
};

// THE ONE PLACE A STORED CODE BECOMES WORDS.
//
// The console used to print the database's own value straight into the cell,
// so operators read `awaiting_pickup`, `co_resident`, `desk_iq_monthly_v1` and
// `__system__`. The values themselves must NOT change — they are the API's
// contract and the filter's query value — so the mapping lives at the edge,
// here, and only the rendering is translated.
//
// Anything absent from the map falls through to underscores-to-spaces and a
// capital first letter. That is the honest default: a new status renders
// readably on the day it is added instead of waiting for this table.
const LABELS: Record<string, string> = {
  // parcels
  awaiting_pickup: 'Awaiting pickup',
  picked_up: 'Picked up',
  returned: 'Returned',
  // occupancy
  co_resident: 'Co-resident',
  primary_resident: 'Primary resident',
  // billing
  past_due: 'Past due',
  incomplete_expired: 'Incomplete (expired)',
  // actors
  __system__: 'System',
};

/** A stored enum or plan code, rendered as the words an operator reads. */
export const labelFor = (value?: string | null): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '—';
  const mapped = LABELS[raw.toLowerCase()];
  if (mapped) return mapped;

  // A plan code (`desk_iq_monthly_v1`) is a machine name with a version
  // suffix. Drop the suffix, then treat it like any other code.
  const words = raw.replace(/_v\d+$/i, '').replace(/_+/g, ' ').trim();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * A status pill that reads the stored value for its tone and renders the
 * human label. Every status cell in the console goes through this, so the
 * two can never drift apart again.
 */
export const StatusTag: React.FC<{ value?: string | null; tone?: Tone }> = ({ value, tone }) => (
  <Tag tone={tone ?? toneFor(value)}>{labelFor(value)}</Tag>
);

/** An id, shortened for a dense row but complete in the title attribute. */
export const Id: React.FC<{ value?: string | number | null }> = ({ value }) => {
  if (value === null || value === undefined || value === '') return <span>—</span>;
  const s = String(value);

  return <Mono title={s}>{s.length > 12 ? `${s.slice(0, 8)}…` : s}</Mono>;
};

/**
 * EVERY TIME IN THE CONSOLE — on the READER's own clock (R-T6): the console is
 * platform-wide and belongs to no building. The zone is passed EXPLICITLY (the
 * device's) rather than left to an implicit local format, and the console says
 * once, under its title, which clock that is (admin-console.tsx). A time that
 * belongs to a building has that building's zone beside it (zoneCell).
 */
export const when = (v?: string | null): string =>
  formatIn(v, deviceZone(), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }) || '—';

/** The same, to the second — for a log line. */
export const whenExact = (v?: string | number | null): string =>
  formatIn(v, deviceZone(), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }) || '—';

/**
 * A BUILDING'S OWN ZONE, named for a person with the key beside it:
 * "Toronto time (UTC-04:00) · America/Toronto". An operator reading a
 * building's row is on their own clock; this is what says the building is not.
 */
export const zoneCell = (zone?: string | null): string => {
  if (!zone) return '—';
  const named = zoneNote(zone);

  return named && named !== zone ? `${named} · ${zone}` : zone;
};

export const num = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString();

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  loadingMore?: boolean;
  error?: string | null;
  emptyLabel?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRowClick?: (row: T) => void;
  caption?: string;
  total?: number | null;
}

export function AdminTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  loadingMore,
  error,
  emptyLabel = 'Nothing here.',
  hasMore,
  onLoadMore,
  onRowClick,
  caption,
  total,
}: Props<T>) {
  // ── INFINITE SCROLL, IN THE ONE PLACE EVERY LIST GOES THROUGH ────────────
  //
  // Every list screen and every detail tab renders this component, so the
  // sentinel lives here rather than in fourteen callers. `rootMargin` starts
  // the next page 300px BEFORE the sentinel is visible, so the rows are already
  // there when the operator arrives instead of a spinner appearing under them.
  //
  // The effect re-runs on `rows.length` for a reason that is easy to miss: when
  // a page arrives and the sentinel is STILL on screen (a short page, a tall
  // window), no new intersection event fires — the element never left. Without
  // re-checking, the list stops one page in and looks like the end of the data.
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || loadingMore || !onLoadMore) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) onLoadMore();
      },
      { rootMargin: '300px' }
    );
    io.observe(el);

    return () => io.disconnect();
  }, [hasMore, loadingMore, onLoadMore, rows.length]);

  if (error) {
    return <ErrorBox role="alert">{error}</ErrorBox>;
  }

  if (loading) {
    return (
      <div aria-busy="true" aria-live="polite">
        <span
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          Loading…
        </span>
        {Array.from({ length: 6 }).map((_, i) => (
          /* eslint-disable-next-line react/no-array-index-key */
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return <Empty>{emptyLabel}</Empty>;
  }

  return (
    <>
      <TableFrame>
        <Scroller>
          <Dense>
          <StickyHead>
          <Table style={{ opacity: loadingMore ? 0.6 : 1 }}>
            {caption ? (
              <caption
                style={{
                  captionSide: 'top',
                  textAlign: 'left',
                  padding: '0 0 8px',
                  color: palette.slate,
                  fontSize: 12,
                }}
              >
                {caption}
                {/*
                  A search does not report a total, and that is on purpose: an
                  exact count of matches over encrypted columns means scanning
                  the whole table on every keystroke. So the caption switches
                  from "N total" to "N shown / N so far" rather than dropping
                  the number entirely, which would read as a broken screen.
                */}
                {typeof total === 'number'
                  ? ` — ${total.toLocaleString()} total`
                  : rows.length
                    ? ` — ${rows.length.toLocaleString()} ${hasMore ? 'so far' : 'found'}`
                    : ''}
              </caption>
            ) : null}
            <Thead>
              <Tr>
                {/* `wide` columns are hidden on narrow viewports through a data
                    attribute (see `Dense` in admin.styled.tsx) rather than by
                    dropping them from the DOM, so the header-to-cell
                    association the engine relies on survives the reflow. */}
                {columns.map(c => (
                  <Th
                    key={c.key}
                    scope="col"
                    numeric={c.numeric}
                    width={c.width}
                    data-wide={c.wide ? 'true' : undefined}
                  >
                    {c.header}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(row => (
                <Tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                  /* A clickable row must be reachable by keyboard, or the
                     console is unusable without a mouse — which is the one
                     thing an operations tool cannot be. */
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                  onKeyDown={
                    onRowClick
                      ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map(c => (
                    <Td
                      key={c.key}
                      numeric={c.numeric}
                      data-label={c.header}
                      data-wide={c.wide ? 'true' : undefined}
                      style={c.nowrap ? { whiteSpace: 'nowrap' } : undefined}
                    >
                      {c.render(row)}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
          </StickyHead>
          </Dense>
        </Scroller>
      </TableFrame>
      {hasMore ? (
        <div
          ref={sentinel}
          style={{ marginTop: 12, textAlign: 'center' }}
          aria-live="polite"
        >
          {/*
            THE BUTTON STAYS, and it is not a leftover.

            The sentinel above it fires `onLoadMore` when it scrolls into view,
            which is the whole point. But IntersectionObserver does not fire in
            three real situations: a browser without it, a container the
            observer cannot resolve, and — the one that actually bites — a table
            short enough that the sentinel is ALREADY on screen and never
            "enters" anything. The effect handles the third by checking on
            mount; the button is what is left for the first two.

            It is also the keyboard path. Scrolling is not a thing you can do
            with a keyboard inside a region that has no focusable element, so a
            keyboard-only operator needs something to press.
          */}
          <Btn onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Btn>
        </div>
      ) : null}
    </>
  );
}
